import { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SafeImage } from '@/components/ui/safe-image';
import { Button } from '@/components/ui/button';
import { ArrowRight, Building2, ChevronLeft, ChevronRight, Map as MapLucideIcon, MapPin, Search } from 'lucide-react';
import { useStoreProfilesSummary, type StoreProfileSummaryRow } from '@/features/store-profiles/hooks/useStoreProfilesData';
import { deriveTwinReadiness, TWIN_READINESS_STATE_LABELS } from '@/features/store-profiles/lib/twinReadiness';
import type { TwinReadiness } from '@/features/store-profiles/types';
import { normalizePhotoFocalPoint, photoObjectPosition } from '@/features/store-profiles/lib/photoFocalPoint';
import { ErrorState } from '@/components/ui/error-state';
import { LoadingState } from '@/components/ui/loading-state';
import { useStorefrontImageSource } from '@/features/store-profiles/hooks/useStorefrontImageSource';

const PAGE_SIZE = 60;
const RETAILER_FILTER_STORAGE_KEY = 'store-profiles:retailer-filter';

type ReadinessStateFilter = TwinReadiness['state'] | 'all';
type NeedsWorkFilter = 'all' | 'missing_floorplan' | 'unmapped_evidence' | 'stale_survey' | 'processing_errors' | 'image_confirmation';

const STALE_SURVEY_DAYS = 90;

function readinessFor(store: StoreProfileSummaryRow): TwinReadiness {
  return deriveTwinReadiness({
    hasStoreIdentity: Boolean(store.storeName && store.address && store.retailerName),
    hasFloorplanImage: store.hasFloorplanImage,
    aisleCount: store.aisleCount,
    zoneCount: store.zoneCount,
    evidenceCount: store.walkthroughCount + store.referenceImageCount,
    pendingSuggestionCount: store.pendingWalkthroughSuggestionCount + store.pendingImageSuggestionCount,
    unmappedEvidenceCount: store.unmappedWalkthroughCount + store.unmappedReferenceImageCount,
    twinPublishedAt: store.twinPublishedAt,
  });
}

function isSurveyStale(store: StoreProfileSummaryRow): boolean {
  if (!store.lastSurveyDate) return true;
  const parsed = Date.parse(store.lastSurveyDate);
  if (Number.isNaN(parsed)) return true;
  return (Date.now() - parsed) / 86_400_000 > STALE_SURVEY_DAYS;
}

function hasUnmappedEvidence(store: StoreProfileSummaryRow): boolean {
  return store.unmappedWalkthroughCount + store.unmappedReferenceImageCount > 0;
}

function readSavedRetailerFilter(): string {
  if (typeof window === 'undefined') return 'all';
  try {
    return window.localStorage.getItem(RETAILER_FILTER_STORAGE_KEY) || 'all';
  } catch {
    return 'all';
  }
}

function saveRetailerFilter(value: string): void {
  try {
    window.localStorage.setItem(RETAILER_FILTER_STORAGE_KEY, value);
  } catch {
    // Keep the filter usable when storage is unavailable or blocked.
  }
}

export default function StoreProfilesListingPage() {
  const [search, setSearch] = useState('');
  const [retailerFilter, setRetailerFilter] = useState<string>(readSavedRetailerFilter);
  const [readinessFilter, setReadinessFilter] = useState<ReadinessStateFilter>('all');
  const [needsWorkFilter, setNeedsWorkFilter] = useState<NeedsWorkFilter>('all');
  const [page, setPage] = useState(1);
  const { summaries, retailers, isLoading, isError, error } = useStoreProfilesSummary();

  useEffect(() => {
    saveRetailerFilter(retailerFilter);
  }, [retailerFilter]);

  useEffect(() => {
    if (
      !isLoading
      && retailerFilter !== 'all'
      && !retailers.some((retailer) => String(retailer.id) === retailerFilter)
    ) {
      setRetailerFilter('all');
    }
  }, [isLoading, retailerFilter, retailers]);

  const readinessByStore = useMemo(
    () => new Map(summaries.map((store) => [store.storeId, readinessFor(store)])),
    [summaries],
  );

  const filtered = useMemo(() => {
    return summaries.filter((store) => {
      const readiness = readinessByStore.get(store.storeId);
      if (readinessFilter !== 'all' && readiness?.state !== readinessFilter) return false;
      if (retailerFilter !== 'all' && store.retailerId !== parseInt(retailerFilter, 10)) return false;
      if (needsWorkFilter === 'missing_floorplan' && store.hasFloorplanImage) return false;
      if (needsWorkFilter === 'unmapped_evidence' && !hasUnmappedEvidence(store)) return false;
      if (needsWorkFilter === 'stale_survey' && !isSurveyStale(store)) return false;
      if (needsWorkFilter === 'processing_errors' && store.missingThumbnailCount === 0) return false;
      if (needsWorkFilter === 'image_confirmation' && store.storefrontPhotoVerificationStatus !== 'to_be_confirmed') return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          store.storeName.toLowerCase().includes(q)
          || store.storeCode.toLowerCase().includes(q)
          || store.address.toLowerCase().includes(q)
          || (store.retailerName ?? '').toLowerCase().includes(q)
          || (store.region ?? '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [summaries, readinessByStore, search, retailerFilter, readinessFilter, needsWorkFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetPage = () => setPage(1);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={Building2}
        title="Store Profiles"
        description="Digital twins of every store — floorplans, aisles, evidence and campaigns, with clear twin-readiness per store."
      />

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden />
          <Input
            placeholder="Search by store, retailer, code or address..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
            className="pl-9"
            aria-label="Search stores"
          />
        </div>
        <Select value={retailerFilter} onValueChange={(v) => { setRetailerFilter(v); resetPage(); }}>
          <SelectTrigger className="w-44" aria-label="Filter by retailer"><SelectValue placeholder="Retailer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All retailers</SelectItem>
            {retailers.map((r) => <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={readinessFilter} onValueChange={(v) => { setReadinessFilter(v as ReadinessStateFilter); resetPage(); }}>
          <SelectTrigger className="w-48" aria-label="Filter by twin readiness"><SelectValue placeholder="Twin readiness" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any readiness</SelectItem>
            {(Object.keys(TWIN_READINESS_STATE_LABELS) as TwinReadiness['state'][]).map((state) => (
              <SelectItem key={state} value={state}>{TWIN_READINESS_STATE_LABELS[state]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={needsWorkFilter} onValueChange={(v) => { setNeedsWorkFilter(v as NeedsWorkFilter); resetPage(); }}>
          <SelectTrigger className="w-52" aria-label="Filter by work needed"><SelectValue placeholder="Needs work" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any state</SelectItem>
            <SelectItem value="missing_floorplan">Missing floorplan</SelectItem>
            <SelectItem value="unmapped_evidence">Has unmapped evidence</SelectItem>
            <SelectItem value="stale_survey">Stale or missing survey</SelectItem>
            <SelectItem value="processing_errors">Processing errors</SelectItem>
            <SelectItem value="image_confirmation">Images to confirm</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <LoadingState message="Loading store twin summary…" />}
      {isError && <ErrorState title="Could not load stores" message={error instanceof Error ? error.message : 'Unknown error'} />}

      {!isLoading && !isError && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              {filtered.length} of {summaries.length} stores
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {pageItems.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" aria-hidden />
                <p className="text-gray-600 font-medium">No stores match your filters</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {pageItems.map((store) => (
                <StoreSummaryCard
                  key={store.storeId}
                  store={store}
                  readiness={readinessByStore.get(store.storeId)!}
                />
              ))}
            </div>
          )}
        </>
      )}

    </div>
  );
}

function StoreSummaryCard({ store, readiness }: { store: StoreProfileSummaryRow; readiness: TwinReadiness }) {
  const storefrontImage = useStorefrontImageSource(store.photoUrl);

  return (
    <Link href={`/super-admin/store-profiles/${store.storeId}`}>
      <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group h-full">
        <div className="relative aspect-[16/9] bg-gray-100">
          {storefrontImage.resolvedUrl ? (
            <SafeImage
              src={storefrontImage.resolvedUrl}
              alt={`${store.storeName} storefront`}
              className="h-full w-full object-cover"
              containerClassName="h-full w-full"
              placeholderType="photo"
              style={{
                objectPosition: photoObjectPosition(
                  normalizePhotoFocalPoint(store.photoFocalX, store.photoFocalY),
                ),
              }}
            />
          ) : storefrontImage.isLoading ? (
            <div className="w-full h-full animate-pulse bg-slate-200 flex items-center justify-center text-slate-500 text-xs">
              Loading storefront…
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <Building2 className="h-10 w-10" aria-hidden />
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold group-hover:text-blue-600 transition-colors truncate">{store.storeName}</p>
              <p className="text-sm text-gray-500 truncate">{store.retailerName ?? '—'} · {store.storeCode}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-blue-500 shrink-0" aria-hidden />
          </div>
          <p className="text-sm text-gray-600 flex items-start gap-1 mt-2">
            <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
            <span className="truncate">{store.address}</span>
          </p>

          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-xs text-gray-600">
            <span className="flex items-center gap-1">
              <MapLucideIcon className="h-3 w-3" aria-hidden />
              {store.hasFloorplanImage ? `${store.aisleCount} aisle${store.aisleCount === 1 ? '' : 's'}` : 'No floorplan'}
            </span>
            <span>{store.walkthroughCount} video{store.walkthroughCount === 1 ? '' : 's'}</span>
            <span>{store.activeCampaignCount} active campaign{store.activeCampaignCount === 1 ? '' : 's'}</span>
          </div>

          <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t">
            {readiness.nextAction ? (
              <span className="flex items-center gap-1 text-xs font-medium text-amber-700">
                Next: {readiness.nextAction.label}
                <ArrowRight className="h-3 w-3" aria-hidden />
              </span>
            ) : (
              <span className="text-xs font-medium text-emerald-700">
                {readiness.state === 'published'
                  ? `Twin ready — published ${store.twinPublishedAt?.slice(0, 10) ?? ''}`
                  : 'Ready to publish'}
              </span>
            )}
            <span className="text-xs text-gray-400">
              {store.lastSurveyDate ? `Surveyed ${store.lastSurveyDate}` : 'No survey'}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
